import createComponent from '@carvana/merch-components/createComponent';
import cx from 'classnames';
import { Fragment } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import useGetConsumerHref from '~/hooks/common/selectors/useGetConsumerHref';
import { nativeSrpPayload } from '~/native/common/utils';
import generateSearchLinkFilter from '~/utilities/generateSearchFilterLink';
import optimizeImage from '~/utilities/optimizeImage';
import styles from './AffordableCarsCard.module.css';
import {
  CardContainer,
  CardCta,
  CardSubtitle,
  CardTitle,
  CardTitleContainer
} from './Cards.styles';

const AFFORDABLE_CARS_SEARCH_LINK = generateSearchLinkFilter({
  filters: { price: { max: 20000 } }
});

const zeroToNine = Array.from({ length: 10 }, (_, i) => i);

const Number = createComponent('div', { className: 'w-[35px] text-center' });

type NumbersProps = {
  start: number;
  stop: number;
};

function Numbers(props: NumbersProps) {
  return (
    <Number
      className={styles.slotMachine}
      style={
        {
          '--start-number': props.start,
          '--stop-number': props.stop
        } as React.CSSProperties
      }
    >
      {zeroToNine.map(num => (
        <Fragment key={num}>
          <div>{num}</div>
          <div className="invisible">{num}</div>
        </Fragment>
      ))}
    </Number>
  );
}

export default function AffordableCarsCard() {
  const getHref = useGetConsumerHref();

  return (
    <CardContainer
      className="bg-background-primary-weak"
      data-analytics-id="Shop Affordable Cars"
      data-testid="affordable-cars-card"
      data-native-payload={nativeSrpPayload}
      href={getHref(AFFORDABLE_CARS_SEARCH_LINK)}
    >
      <CardTitleContainer>
        <CardTitle>Cars under $20K</CardTitle>
        <CardSubtitle>
          Great rides with quality you can count on — at a price you’ll love.
        </CardSubtitle>
      </CardTitleContainer>
      <div className="flex-1">
        <div
          className={cx(
            'relative h-[100px] overflow-hidden text-text-strong-active',
            styles.fadeIn
          )}
        >
          <div className="absolute top-[15px] flex t-brand-xl">
            <Number>$</Number>
            <Numbers start={6} stop={1} />
            <Numbers start={2} stop={8} />
            <div className="-mr-4 ml-4">,</div>
            <Numbers start={8} stop={3} />
            <Numbers start={9} stop={2} />
            <Numbers start={0} stop={5} />
          </div>
          <div className={cx('absolute inset-0', styles.overlay)} />
        </div>
        <img
          src={optimizeImage(
            `${appAssetsCdnUrl}/images/home/budget-affordable-cars.png`,
            { format: 'auto' }
          )}
          alt="A silver SUV"
          className="relative -mt-[43px]"
          loading="lazy"
        />
      </div>
      <CardCta data-testid="explore-budget-options">
        Explore Budget Options
      </CardCta>
    </CardContainer>
  );
}
